import React from "react";
import { useParams } from "react-router-dom";
import { Page } from "@iq/models";
import AddAttributesModal from "../../modals/AddAttributes/AddAttributesModal";
import AddIncompatibility from "../../modals/Incompatibility/AddIncompatibility";
import ServiceMedia from "./ServiceMedia/ServiceMedia";
import ServiceOverview from "./ServiceOverview";
import useProductCatalogStore from "../../states/productCatalog";
import AttributeDetails from "./AttributeDetails/AttributeDetails";
import { getProducts, updateProductDetails, uploadProductImage, deleteProductImage, deleteProductAttribute, updateAttributeQueue } from "../../services/productService";
import styles from "./variableProductPage.module.css";

export default function VariableProductPage() {
    const { id } = useParams();
    const products = useProductCatalogStore((state) => state.products);
    const loading = useProductCatalogStore((state) => state.loading);
    const error = useProductCatalogStore((state) => state.error);
    const fetchProducts = useProductCatalogStore((state) => state.fetchProducts);
    const [isAddAttributesModalOpen, setIsAddAttributesModalOpen] = React.useState(false);
    const [selectedAttributeId, setSelectedAttributeId] = React.useState(null);
    const [isAddIncompatibilityModalOpen, setIsAddIncompatibilityModalOpen] = React.useState(false);

    React.useEffect(() => {
        if (useProductCatalogStore.getState().products.length === 0) {
            fetchProducts();
        }
    }, [id, fetchProducts]);

    const product = products.find((item) => String(item?.id ?? item?.ID ?? "") === String(id));
    const productImages = React.useMemo(() => {
        const rawImages = product?.images?.length ? product.images : product?.image ? [product.image] : [];
        return rawImages.map((image) => typeof image === "string"
            ? { id: null, url: image, alt_text: "" }
            : { id: image?.id ?? image?.ID ?? null, url: image?.url ?? "", alt_text: image?.alt_text ?? "" }
        ).filter((image) => Boolean(image.url));
    }, [product]);

    const handleSaveProduct = async (details) => {
        await updateProductDetails(id, details);
        const store = useProductCatalogStore.getState();
        store.setProducts(store.products.map((item) => (
            String(item?.id ?? item?.ID ?? "") === String(id)
                ? { ...item, ...details, pricing: { display: `$${(details.base_price / 100).toFixed(2)}` } }
                : item
        )));
    };

    const handleReorderAttributes = async (orderedIds) => {
        const queuedAttributes = orderedIds.map((attributeId, queue) => ({
            attribute_id: Number(attributeId), queue,
        }));
        if (queuedAttributes.some(({ attribute_id }) => !Number.isInteger(attribute_id) || attribute_id <= 0)) {
            throw new Error("Attribute order could not be saved because an attribute id is invalid.");
        }
        await updateAttributeQueue(id, queuedAttributes);
    };

    if (!product) {
        return (
            <Page>
                <div className={styles.page}>
                    <p role={error ? "alert" : "status"}>{loading ? "Loading service..." : error || "No service found."}</p>
                    {error && <button type="button" onClick={fetchProducts}>Try again</button>}
                </div>
            </Page>
        );
    }

    return (
        <Page>
            <div className={styles.page}>
                {error && <p role="alert">{error}</p>}
                <div className={styles.overview}>
                    <ServiceOverview key={id} product={product} onSave={handleSaveProduct} />
                    <ServiceMedia
                        images={productImages}
                        onUploadImage={async (file) => {
                            await uploadProductImage(id, file, { altText: product.name });
                            await fetchProducts();
                        }}
                        onDeleteImage={async (imageId) => {
                            await deleteProductImage(id, imageId);
                            await fetchProducts();
                        }}
                    />
                </div>
                <div className={styles.attributes}>
                    <AttributeDetails
                        productId={Number(id)}
                        attributes={product.attributes || []}
                        onAddAttribute={() => {
                            setSelectedAttributeId(null);
                            setIsAddAttributesModalOpen(true);
                        }}
                        onEditAttribute={(attributeId) => {
                            setSelectedAttributeId(attributeId);
                            setIsAddAttributesModalOpen(true);
                        }}
                        onAddIncompatibility={() => setIsAddIncompatibilityModalOpen(true)}
                        onReorderAttributes={handleReorderAttributes}
                        onDeleteAttribute={async (attributeId) => {
                            await deleteProductAttribute(id, attributeId);
                            await fetchProducts();
                            const refreshError = useProductCatalogStore.getState().error;
                            if (refreshError) {
                                throw new Error(`Attribute group removed, but the catalog could not be refreshed: ${refreshError}`);
                            }
                        }}
                    />
                </div>
            </div>
            <AddAttributesModal
                isOpen={isAddAttributesModalOpen}
                productId={Number(id)}
                attributes={product.attributes || []}
                selectedAttributeId={selectedAttributeId}
                onClose={() => setIsAddAttributesModalOpen(false)}
                onSaved={async () => {
                    const payload = await getProducts();
                    useProductCatalogStore.getState().setProducts(payload?.products ?? payload ?? []);
                    setIsAddAttributesModalOpen(false);
                }}
            />
            <AddIncompatibility
                isOpen={isAddIncompatibilityModalOpen}
                productId={Number(id)}
                attributes={product.attributes || []}
                onClose={() => setIsAddIncompatibilityModalOpen(false)}
                onChanged={async () => {
                    const payload = await getProducts();
                    useProductCatalogStore.getState().setProducts(payload?.products ?? payload ?? []);
                }}
            />
        </Page>
    );
}
